import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from '../../../axios';
import {
  Container,
  Card,
  CardHeader,
  CardContent,
  Typography,
  Box,
  TextField,
  MenuItem,
  Button,
  Chip,
  useTheme,
  useMediaQuery,
  Paper,
  Grid,
  Autocomplete,
} from '@mui/material';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import RefreshIcon from '@mui/icons-material/Refresh';
import InventoryIcon from '@mui/icons-material/Inventory';
import SearchIcon from '@mui/icons-material/Search';

export default function InventoryReport() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  const companyId = parseInt(localStorage.getItem('companyId'), 10);
  
  const [products, setProducts] = useState([]);
  const [branches, setBranches] = useState([]);
  const [stocks, setStocks] = useState([]);
  
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [branchId, setBranchId] = useState('');
  const [stockId, setStockId] = useState('');
  
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [productsLoading, setProductsLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setProductsLoading(true);
      try {
        const [productsRes, branchesRes, stocksRes] = await Promise.all([
          axios.get(`/Product/by-company/${companyId}`),
          axios.get(`/Branch/by-company/${companyId}`),
          axios.get(`/Stock/by-company/${companyId}`)
        ]);
        setProducts(productsRes.data);
        setBranches(branchesRes.data);
        setStocks(stocksRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setProductsLoading(false);
      }
    };
    fetchData();
  }, [companyId]);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        ...(selectedProduct && { productId: selectedProduct.id }),
        ...(branchId && { branchId }),
        ...(stockId && { stockId }),
      };
      const res = await axios.get(
        `/StockMovements/inventory-report/${companyId}`,
        { params }
      );
      
      const data = res.data.map((r, i) => ({
        id: i,
        productName: r.productName || r.ProductName,
        stockName: r.stockName || r.StockName,
        branchName: r.branchName || r.BranchName,
        totalIn: r.totalIn !== undefined ? r.totalIn : r.TotalIn,
        totalOut: r.totalOut !== undefined ? r.totalOut : r.TotalOut,
        closingStock: r.closingStock !== undefined ? r.closingStock : r.ClosingStock,
        purchasePrice: r.purchasePrice !== undefined ? r.purchasePrice : (r.PurchasePrice !== undefined ? r.PurchasePrice : 0),
        totalPurchaseCost: r.totalPurchaseCost !== undefined ? r.totalPurchaseCost : (r.TotalPurchaseCost !== undefined ? r.TotalPurchaseCost : 0),
        salePrice: r.salePrice !== undefined ? r.salePrice : (r.SalePrice !== undefined ? r.SalePrice : 0),
        totalSaleValue: r.totalSaleValue !== undefined ? r.totalSaleValue : (r.TotalSaleValue !== undefined ? r.TotalSaleValue : 0),
      }));
      
      setRows(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [companyId, selectedProduct, branchId, stockId]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  // حساب الإحصائيات
  const statistics = useMemo(() => ({
    totalItems: rows.length,
    totalStock: rows.reduce((sum, row) => sum + row.closingStock, 0),
    totalValue: rows.reduce((sum, row) => sum + row.totalSaleValue, 0),
    totalCost: rows.reduce((sum, row) => sum + row.totalPurchaseCost, 0),
    negativeStock: rows.filter(row => row.closingStock < 0).length,
  }), [rows]);

  const columns = [
    { 
      field: 'productName', 
      headerName: 'الصنف', 
      flex: 1, 
      minWidth: isMobile ? 150 : 200, 
      align: 'center', 
      headerAlign: 'center' 
    },
    !isMobile && { 
      field: 'stockName', 
      headerName: 'المستودع', 
      width: 150, 
      align: 'center', 
      headerAlign: 'center' 
    },
    !isMobile && { 
      field: 'branchName', 
      headerName: 'الفرع', 
      width: 150, 
      align: 'center', 
      headerAlign: 'center' 
    },
    { 
      field: 'totalIn', 
      headerName: 'الوارد', 
      width: isMobile ? 70 : 100, 
      align: 'center', 
      headerAlign: 'center',
      renderCell: (params) => (
        <Chip 
          label={params.value} 
          size="small" 
          color="success" 
          variant="outlined"
        />
      )
    },
    { 
      field: 'totalOut', 
      headerName: 'الصادر', 
      width: isMobile ? 70 : 100, 
      align: 'center', 
      headerAlign: 'center',
      renderCell: (params) => (
        <Chip 
          label={params.value} 
          size="small" 
          color="error" 
          variant="outlined"
        />
      )
    },
    { 
      field: 'closingStock', 
      headerName: 'الرصيد', 
      width: isMobile ? 80 : 100, 
      align: 'center', 
      headerAlign: 'center',
      renderCell: (params) => {
        const value = params.row.closingStock;
        const color = value < 0 ? 'error' : value === 0 ? 'default' : 'success';
        return (
          <Chip 
            label={value} 
            size="small" 
            color={color}
            sx={{ fontWeight: 'bold' }}
          />
        );
      }
    },
    !isMobile && { 
      field: 'purchasePrice', 
      headerName: 'سعر الشراء', 
      width: 120, 
      align: 'center', 
      headerAlign: 'center',
      renderCell: (params) => {
        const val = params.row.purchasePrice || 0;
        return (
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {val.toFixed(2)}
          </Typography>
        );
      }
    },
    !isMobile && { 
      field: 'totalPurchaseCost', 
      headerName: 'إجمالي التكلفة', 
      width: 140, 
      align: 'center', 
      headerAlign: 'center',
      renderCell: (params) => {
        const value = params.row.totalPurchaseCost || 0;
        const color = value < 0 ? 'error.main' : 'success.main';
        return (
          <Typography variant="body2" sx={{ color, fontWeight: 600 }}>
            {value.toFixed(2)}
          </Typography>
        );
      }
    },
    !isMobile && { 
      field: 'salePrice', 
      headerName: 'سعر البيع', 
      width: 120, 
      align: 'center', 
      headerAlign: 'center',
      renderCell: (params) => {
        const val = params.row.salePrice || 0;
        return (
          <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 600 }}>
            {val.toFixed(2)}
          </Typography>
        );
      }
    },
    { 
      field: 'totalSaleValue', 
      headerName: 'قيمة البيع', 
      width: isMobile ? 90 : 150, 
      align: 'center', 
      headerAlign: 'center',
      renderCell: (params) => {
        const value = params.row.totalSaleValue || 0;
        const color = value < 0 ? 'error.main' : 'success.main';
        return (
          <Typography variant="body2" sx={{ color, fontWeight: 700 }}>
            {value.toFixed(2)}
          </Typography>
        );
      }
    },
  ].filter(Boolean);

  return (
    <Container maxWidth="xl" dir="rtl" sx={{ py: { xs: 2, md: 4 } }}>
      <Card elevation={3} sx={{ borderRadius: 2 }}>
        <CardHeader
          avatar={<InventoryIcon sx={{ fontSize: 32, color: 'white' }} />}
          title={
            <Typography variant={isMobile ? 'h6' : 'h5'} fontWeight="bold">
              تقرير جرد المخزون
            </Typography>
          }
          sx={{ 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white'
          }}
        />
        
        <CardContent>
          {/* الفلاتر */}
          <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 2, bgcolor: 'grey.50' }}>
            <Grid container spacing={2} alignItems="center">
              {/* البحث عن الصنف */}
              <Grid item xs={12} sm={6} md={3}>
                <Autocomplete
                  value={selectedProduct}
                  onChange={(event, newValue) => {
                    setSelectedProduct(newValue);
                  }}
                  options={products}
                  getOptionLabel={(option) => option.arabicName || ''}
                  loading={productsLoading}
                  loadingText="جاري التحميل..."
                  noOptionsText="لا توجد نتائج"
                  size="small"
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="ابحث عن صنف"
                      placeholder="اكتب للبحث..."
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <SearchIcon sx={{ color: 'action.active', mr: 1, my: 0.5 }} />
                            {params.InputProps.startAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography variant="body2">{option.arabicName}</Typography>
                        {option.code && (
                          <Typography variant="caption" color="text.secondary">
                            كود: {option.code}
                          </Typography>
                        )}
                      </Box>
                    </li>
                  )}
                />
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  select
                  label="اختر فرعًا"
                  size="small"
                  fullWidth
                  value={branchId}
                  onChange={e => setBranchId(e.target.value)}
                >
                  <MenuItem value="">كل الفروع</MenuItem>
                  {branches.map(b => (
                    <MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  select
                  label="اختر مستودعًا"
                  size="small"
                  fullWidth
                  value={stockId}
                  onChange={e => setStockId(e.target.value)}
                >
                  <MenuItem value="">كل المستودعات</MenuItem>
                  {stocks.map(s => (
                    <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <Button 
                  variant="contained" 
                  fullWidth
                  startIcon={<RefreshIcon />}
                  onClick={fetchReport}
                  sx={{ height: 40 }}
                >
                  تحديث
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {/* الإحصائيات */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} sm={4} md={2.4}>
              <Paper elevation={2} sx={{ p: 2, textAlign: 'center', borderRadius: 2, bgcolor: 'primary.light' }}>
                <Typography variant="caption" color="white">عدد الأصناف</Typography>
                <Typography variant="h6" fontWeight="bold" color="white">
                  {statistics.totalItems}
                </Typography>
              </Paper>
            </Grid>
            
            <Grid item xs={6} sm={4} md={2.4}>
              <Paper elevation={2} sx={{ p: 2, textAlign: 'center', borderRadius: 2, bgcolor: 'success.light' }}>
                <Typography variant="caption" color="white">إجمالي الرصيد</Typography>
                <Typography variant="h6" fontWeight="bold" color="white">
                  {statistics.totalStock.toFixed(0)}
                </Typography>
              </Paper>
            </Grid>
            
            <Grid item xs={6} sm={4} md={2.4}>
              <Paper elevation={2} sx={{ p: 2, textAlign: 'center', borderRadius: 2, bgcolor: 'warning.light' }}>
                <Typography variant="caption" color="white">إجمالي التكلفة</Typography>
                <Typography variant="h6" fontWeight="bold" color="white">
                  {statistics.totalCost.toFixed(2)}
                </Typography>
              </Paper>
            </Grid>
            
            <Grid item xs={6} sm={4} md={2.4}>
              <Paper elevation={2} sx={{ p: 2, textAlign: 'center', borderRadius: 2, bgcolor: 'info.light' }}>
                <Typography variant="caption" color="white">إجمالي القيمة</Typography>
                <Typography variant="h6" fontWeight="bold" color="white">
                  {statistics.totalValue.toFixed(2)}
                </Typography>
              </Paper>
            </Grid>
            
            <Grid item xs={6} sm={4} md={2.4}>
              <Paper elevation={2} sx={{ p: 2, textAlign: 'center', borderRadius: 2, bgcolor: 'error.light' }}>
                <Typography variant="caption" color="white">أرصدة سالبة</Typography>
                <Typography variant="h6" fontWeight="bold" color="white">
                  {statistics.negativeStock}
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          {/* الجدول */}
          <Box sx={{ 
            height: isMobile ? 500 : 600, 
            width: '100%',
            '& .MuiDataGrid-root': {
              border: 'none',
              borderRadius: 2,
            },
            '& .MuiDataGrid-cell': {
              borderBottom: '1px solid #f0f0f0',
            },
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: '#f5f5f5',
              borderRadius: '8px 8px 0 0',
              fontWeight: 'bold',
            },
          }}>
            <DataGrid
              rows={rows}
              columns={columns}
              loading={loading}
              components={{ Toolbar: GridToolbar }}
              pageSize={isMobile ? 10 : 25}
              rowsPerPageOptions={isMobile ? [10, 25] : [25, 50, 100]}
              disableSelectionOnClick
              density={isMobile ? 'compact' : 'standard'}
              sx={{
                '& .MuiDataGrid-cell': { 
                  textAlign: 'center',
                  fontSize: isMobile ? '0.75rem' : '0.875rem'
                },
                '& .MuiDataGrid-columnHeader': { 
                  textAlign: 'center',
                  fontWeight: 'bold',
                  fontSize: isMobile ? '0.75rem' : '0.875rem'
                }
              }}
            />
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
}